import {
  CSSProperties,
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import { isEqual } from 'lodash'
import useNotifications from '@hooks/useNotifications'
import { isMobile } from '@const/device'
import Modal from '@organisms/Modal'
import { Wrapper } from './styled'
import Notification from './Notification'
import DownButton from './DownButton'

// Отступ плашки от колокольчика и от нижнего края окна
const PANEL_GAP = 12

type Props = {
  listIsVisible: boolean
  toggleListIsVisible: Dispatch<SetStateAction<boolean>>
  anchorRef: RefObject<HTMLDivElement>
}

const List: FC<Props> = ({
  listIsVisible,
  toggleListIsVisible,
  anchorRef,
}) => {
  const { data, setPage } = useNotifications()
  const { items = [] } = data || {}

  const [itemsState, setItemsState] = useState(items)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>()

  const needShowMore = items.length > 0 && items.length % 20 === 0

  useEffect(() => {
    if (!isEqual(items, itemsState)) {
      setItemsState((prev) => [...prev, ...items])
    }
  }, [items])

  // Плашка позиционируется fixed (иначе её режет контейнер страницы с
  // overflow: hidden), поэтому привязываем её к колокольчику руками: ставим под
  // ним и равняем по его правому краю, чтобы не перекрывать шапку.
  useLayoutEffect(() => {
    if (isMobile || !listIsVisible) return undefined

    const updatePanelPosition = () => {
      const anchor = anchorRef.current?.getBoundingClientRect()

      if (!anchor) return

      const top = Math.round(anchor.bottom + PANEL_GAP)

      setPanelStyle({
        top,
        right: Math.round(window.innerWidth - anchor.right),
        maxHeight: Math.max(window.innerHeight - top - PANEL_GAP, 0),
      })
    }

    updatePanelPosition()
    window.addEventListener('resize', updatePanelPosition)
    window.addEventListener('scroll', updatePanelPosition, true)

    return () => {
      window.removeEventListener('resize', updatePanelPosition)
      window.removeEventListener('scroll', updatePanelPosition, true)
    }
  }, [anchorRef, listIsVisible])

  if (!listIsVisible || !data || items.length === 0) {
    return null
  }

  const list = (
    <Wrapper style={isMobile ? undefined : panelStyle}>
      {itemsState.map((notification) => (
        <Notification key={notification.id} notification={notification} />
      ))}
      {needShowMore && (
        <DownButton onClick={() => setPage((prev) => prev + 1)} />
      )}
    </Wrapper>
  )

  return (
    <>
      {isMobile ? (
        <Modal
          visible={listIsVisible}
          onModalClose={() => toggleListIsVisible(false)}
          withoutWhiteBackground
        >
          {list}
        </Modal>
      ) : (
        list
      )}
    </>
  )
}

export default List
